/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import {
  Check,
  Minus,
  MessageSquare,
  Users,
  Boxes,
  Database,
  BadgeCheck,
  Sparkles,
} from 'lucide-react'
import Navbar from '@/components/landing/Navbar'
import { FinalCta } from '@/components/landing/FinalCta'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { useStore } from '@/store/Store'
import { useRequestPlanMutation } from '@/hooks/useRequestPlan'
import { choosePlan } from '@/lib/choosePlan'
import { toast } from 'sonner'
import type { PlanKey } from '@/hooks/usePlans'

type Plan = {
  key: 'monthly' | 'yearly' | 'lifetime'
  backendKey: PlanKey
  name: string
  label: string
  priceShort: string
  priceNote?: string
  featured?: boolean
  description: string
  idealFor: string
  highlights: string[]
  specs: Array<{
    icon: React.ReactNode
    label: string
    value: string
  }>
  limits?: string[]
  cta: string
  to: string
}

type MessagingPlan = {
  key: 'monthly' | 'yearly'
  name: string
  priceShort: string
  description: string
  features: string[]
  cta: string
}

type WaPack = {
  name: string
  qty: number
  priceShort: string
  unitLabel: string
  featured?: boolean
}

const PLANS: Plan[] = [
  {
    key: 'monthly',
    backendKey: 'MONTHLY_STANDARD',
    name: 'Standard',
    label: 'Mensuel',
    priceShort: '30 000 CFA / mois',
    priceNote: 'Souple pour démarrer',
    description:
      'Le plan simple pour bien gérer vos ventes, vos paiements, vos clients et votre stock sans complexité inutile.',
    idealFor: 'Petits commerces et équipes qui veulent démarrer proprement.',
    highlights: [
      'Gestion des clients, factures, paiements et dettes',
      'Stock structuré avec dépôts et conteneurs',
      'Dépenses et vue de pilotage',
      'Relances email disponibles',
    ],
    specs: [
      { icon: <Users className='h-4 w-4' />, label: 'Sous-comptes', value: '1' },
      { icon: <Boxes className='h-4 w-4' />, label: 'Produits', value: '1 000' },
      { icon: <Database className='h-4 w-4' />, label: 'Stockage', value: '1 Go' },
      { icon: <MessageSquare className='h-4 w-4' />, label: 'WhatsApp inclus', value: '0' },
    ],
    limits: [
      'WhatsApp disponible via packs',
      'Activation messaging selon configuration',
    ],
    cta: 'Choisir Standard',
    to: '/register?redirect=/dashboard&plan=MONTHLY_STANDARD',
  },
  {
    key: 'yearly',
    backendKey: 'YEARLY_ECO',
    name: 'Eco',
    label: 'Annuel',
    priceShort: '300 000 CFA / an',
    priceNote: 'Le meilleur équilibre',
    featured: true,
    description:
      "Le plan le plus intéressant pour une activité déjà installée qui veut plus de capacité, plus d'équipe et une meilleure visibilité.",
    idealFor: 'Commerces en croissance, boutiques plus structurées, petites équipes.',
    highlights: [
      "Plus de volume produit et plus de capacité d'équipe",
      'Meilleur coût sur la durée',
      "200 messages WhatsApp inclus sur l'année",
      'Support prioritaire selon organisation',
    ],
    specs: [
      { icon: <Users className='h-4 w-4' />, label: 'Sous-comptes', value: '5' },
      { icon: <Boxes className='h-4 w-4' />, label: 'Produits', value: '5 000' },
      { icon: <Database className='h-4 w-4' />, label: 'Stockage', value: '5 Go' },
      { icon: <MessageSquare className='h-4 w-4' />, label: 'WhatsApp / an', value: '200 inclus' },
    ],
    cta: 'Choisir Eco',
    to: '/register?redirect=/dashboard&plan=YEARLY_ECO',
  },
  {
    key: 'lifetime',
    backendKey: 'LIFETIME_PREMIUM',
    name: 'Premium',
    label: 'Annuel',
    priceShort: '600 000 CFA / an',
    priceNote: 'soit 50 000 CFA / mois',
    description:
      'Le plan pour les équipes qui ont besoin de plus de capacité, plus de collaborateurs et des relances WhatsApp incluses.',
    idealFor:
      'Entreprises multi-utilisateurs, boutiques avec un large catalogue et une forte activité de recouvrement.',
    highlights: [
      "600 messages WhatsApp inclus sur l'année",
      'Capacité large pour équipe et catalogue',
      "Relances automatiques activées d'entrée",
      'Support prioritaire',
    ],
    specs: [
      { icon: <Users className='h-4 w-4' />, label: 'Sous-comptes', value: '20' },
      { icon: <Boxes className='h-4 w-4' />, label: 'Produits', value: '20 000' },
      { icon: <Database className='h-4 w-4' />, label: 'Stockage', value: '20 Go' },
      { icon: <MessageSquare className='h-4 w-4' />, label: 'WhatsApp / an', value: '600 inclus' },
    ],
    cta: 'Choisir Premium',
    to: '/register?redirect=/dashboard&plan=LIFETIME_PREMIUM',
  },
]

const MESSAGING_PLANS: MessagingPlan[] = [
  {
    key: 'monthly',
    name: 'Messaging mensuel',
    priceShort: 'En option',
    description:
      'Activez les relances WhatsApp selon votre volume et votre besoin de recouvrement.',
    features: [
      'Activation des relances WhatsApp selon configuration',
      'Suivi de consommation par canal',
      'Historique des envois et relances',
      "Compatible avec l'achat de packs WhatsApp",
    ],
    cta: 'Disponible en option',
  },
  {
    key: 'yearly',
    name: 'Messaging annuel',
    priceShort: 'En option',
    description:
      'Pour les activités qui utilisent régulièrement les relances et veulent mieux maîtriser leur usage dans le temps.',
    features: [
      'Logique adaptée à un usage récurrent',
      'Meilleure visibilité sur la durée',
      'Suivi des quotas et de la consommation',
      'Compatible avec les packs WhatsApp',
    ],
    cta: 'Disponible en option',
  },
]

const WA_PACKS: WaPack[] = [
  {
    name: 'Pack Starter',
    qty: 50,
    priceShort: '2 000 CFA',
    unitLabel: '40 CFA / message',
  },
  {
    name: 'Pack Standard',
    qty: 150,
    priceShort: '5 000 CFA',
    unitLabel: '33 CFA / message',
    featured: true,
  },
  {
    name: 'Pack Pro',
    qty: 500,
    priceShort: '15 000 CFA',
    unitLabel: '30 CFA / message',
  },
]

const PRICING_FAQ = [
  {
    q: 'Puis-je commencer gratuitement ?',
    a: "Oui. Vous pouvez créer votre compte et utiliser l'essai avant d'activer un plan.",
  },
  {
    q: 'Les messages WhatsApp sont-ils inclus dans le plan principal ?',
    a: 'Partiellement. Les plans Eco et Premium incluent un quota WhatsApp annuel. Le plan Standard démarre sans WhatsApp — des packs peuvent être achetés séparément.',
  },
  {
    q: 'Que comprend le quota WhatsApp du plan Premium ?',
    a: "Le plan Premium inclut 600 messages WhatsApp sur l'année. Une fois ce quota épuisé, vous pouvez acheter un pack pour continuer.",
  },
  {
    q: 'Puis-je acheter des messages WhatsApp supplémentaires ?',
    a: 'Oui. Des packs WhatsApp peuvent être ajoutés à tout moment selon vos besoins, sans changer de plan principal.',
  },
  {
    q: 'Puis-je changer de plan plus tard ?',
    a: 'Oui. Vous pouvez démarrer avec un plan simple puis passer à un niveau supérieur quand votre activité grandit.',
  },
]

export default function PricingPage() {
  return (
    <div className='min-h-dvh bg-background'>
      <Navbar />

      <section className='relative overflow-hidden border-b'>
        <div className='absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_-10%,hsl(var(--primary)/.18),transparent_60%)]' />
        <div className='relative mx-auto max-w-7xl px-4 pt-16 pb-12 md:pt-20 md:pb-16'>
          <div className='text-center max-w-3xl mx-auto'>
            <p className='inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs text-muted-foreground'>
              Essai gratuit • Pas de carte bancaire
            </p>

            <h1 className='mt-4 text-4xl md:text-5xl font-extrabold tracking-tight'>
              Choisissez le plan adapté à votre{' '}
              <span className='text-primary'>commerce</span>
            </h1>

            <p className='mt-4 text-base md:text-lg text-muted-foreground'>
              Commencez avec l'essentiel : clients, factures, paiements, stock
              et suivi des dettes. Ajoutez ensuite la messagerie WhatsApp
              selon votre usage.
            </p>
          </div>

          <div className='mt-8 grid gap-4 md:grid-cols-3 max-w-5xl mx-auto'>
            <TrustPill
              title='Vendez plus sereinement'
              desc='Factures, encaissements et suivi des dettes dans un seul outil.'
            />
            <TrustPill
              title='Gardez le contrôle sur le stock'
              desc='Produits, dépôts, conteneurs et mouvements bien structurés.'
            />
            <TrustPill
              title='Activez les relances au bon moment'
              desc='Ajoutez la messagerie WhatsApp selon le volume réel de votre activité.'
            />
          </div>
        </div>
      </section>

      <section className='mx-auto max-w-7xl px-4 py-12 md:py-16'>
        <div className='text-center max-w-2xl mx-auto'>
          <Badge variant='secondary'>Plans MOGESAPP</Badge>
          <h2 className='mt-3 text-3xl md:text-4xl font-extrabold tracking-tight'>
            Le cœur de votre gestion commerciale
          </h2>
          <p className='mt-3 text-muted-foreground'>
            Tous les plans vous permettent de gérer votre activité. Ce qui
            change, c'est surtout la capacité, la taille de votre équipe et le
            volume que vous pouvez absorber.
          </p>
        </div>

        <div className='mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3'>
          {PLANS.map((plan) => (
            <PlanCard key={plan.key} plan={plan} />
          ))}
        </div>
      </section>

      <section className='mx-auto max-w-7xl px-4 pb-12 md:pb-16'>
        <div className='rounded-2xl border bg-card'>
          <div className='p-6 md:p-8'>
            <div className='max-w-2xl'>
              <Badge variant='secondary'>Comparatif rapide</Badge>
              <h3 className='mt-3 text-2xl font-bold tracking-tight'>
                Comparez les points essentiels
              </h3>
              <p className='mt-2 text-sm text-muted-foreground'>
                Les différences importantes tiennent à la capacité, à la taille
                de l'équipe et au bonus éventuel de bienvenue.
              </p>
            </div>

            <div className='mt-6 overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b text-left'>
                    <th className='py-3 pr-4 font-semibold'>Élément</th>
                    <th className='py-3 px-4 font-semibold'>Standard</th>
                    <th className='py-3 px-4 font-semibold'>Eco</th>
                    <th className='py-3 pl-4 font-semibold'>Premium</th>
                  </tr>
                </thead>
                <tbody>
                  <PricingRow
                    label='Sous-comptes'
                    monthly='1'
                    yearly='5'
                    lifetime='20'
                  />
                  <PricingRow
                    label='Produits'
                    monthly='1 000'
                    yearly='5 000'
                    lifetime='20 000'
                  />
                  <PricingRow
                    label='Stockage'
                    monthly='1 Go'
                    yearly='5 Go'
                    lifetime='20 Go'
                  />
                  <PricingRow
                    label='WhatsApp inclus / an'
                    monthly='0'
                    yearly='200'
                    lifetime='600'
                  />
                  <PricingRow
                    label='Idéal pour'
                    monthly='Démarrer proprement'
                    yearly='Grandir sereinement'
                    lifetime='Investir sur le long terme'
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className='mx-auto max-w-7xl px-4 pb-12 md:pb-16'>
        <div className='rounded-2xl border bg-muted/30 p-6 md:p-8'>
          <div className='max-w-3xl'>
            <Badge variant='secondary'>Messaging en option</Badge>
            <h2 className='mt-3 text-3xl font-extrabold tracking-tight'>
              Activez la messagerie selon vos besoins
            </h2>
            <p className='mt-3 text-muted-foreground'>
              Le cœur de MOGESAPP couvre déjà votre gestion commerciale. Les
              relances avancées et les canaux payants peuvent être activés
              séparément selon votre volume et votre besoin d'automatisation.
            </p>
          </div>

          <div className='mt-8 grid grid-cols-1 gap-6 md:grid-cols-2'>
            {MESSAGING_PLANS.map((plan) => (
              <Card key={plan.key} className='rounded-2xl border shadow-sm'>
                <CardHeader>
                  <CardTitle className='flex items-center justify-between gap-3'>
                    <span>{plan.name}</span>
                    <Badge variant='outline'>{plan.priceShort}</Badge>
                  </CardTitle>
                  <p className='text-sm text-muted-foreground'>
                    {plan.description}
                  </p>
                </CardHeader>
                <CardContent>
                  <ul className='space-y-2 text-sm'>
                    {plan.features.map((feature, index) => (
                      <li key={index} className='flex items-start gap-2'>
                        <Check className='mt-0.5 h-4 w-4 text-primary' />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className='mt-6'>
                    <Button variant='outline' className='w-full'>
                      {plan.cta}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className='mt-6 rounded-xl border bg-background p-4 text-sm text-muted-foreground'>
            Les messages WhatsApp sont inclus selon le plan. Des packs peuvent
            être achetés séparément pour correspondre à votre consommation réelle.
          </div>
        </div>
      </section>

      <section className='mx-auto max-w-7xl px-4 pb-12 md:pb-16'>
        <div className='rounded-2xl border bg-card p-6 md:p-8'>
          <div className='max-w-3xl'>
            <Badge variant='secondary'>Packs WhatsApp</Badge>
            <h2 className='mt-3 text-3xl font-extrabold tracking-tight'>
              Achetez vos messages WhatsApp selon votre volume
            </h2>
            <p className='mt-3 text-muted-foreground'>
              Choisissez un pack adapté à votre rythme de relances. Achetez
              uniquement ce dont vous avez besoin, sans changer de plan.
            </p>
          </div>

          <div className='mt-8 grid grid-cols-1 gap-6 md:grid-cols-3'>
            {WA_PACKS.map((pack) => (
              <Card
                key={pack.name}
                className={clsx(
                  'rounded-2xl border shadow-sm',
                  pack.featured && 'ring-2 ring-primary/50',
                )}
              >
                <CardHeader className='relative'>
                  {pack.featured ? (
                    <div className='absolute -top-7 left-1/2 -translate-x-1/2'>
                      <Badge className='rounded-full px-3 py-1'>
                        Le plus choisi
                      </Badge>
                    </div>
                  ) : null}

                  <CardTitle className='text-xl font-bold'>
                    {pack.name}
                  </CardTitle>
                  <div className='text-3xl font-extrabold'>
                    {pack.priceShort}
                  </div>
                  <p className='text-sm text-muted-foreground'>
                    {pack.qty.toLocaleString('fr-FR')} messages WhatsApp
                  </p>
                </CardHeader>

                <CardContent>
                  <div className='rounded-xl border bg-muted/30 p-4'>
                    <div className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                      Prix indicatif
                    </div>
                    <div className='mt-1 text-sm font-medium'>
                      {pack.unitLabel}
                    </div>
                  </div>

                  <ul className='mt-4 space-y-2 text-sm'>
                    <li className='flex items-start gap-2'>
                      <Check className='mt-0.5 h-4 w-4 text-primary' />
                      <span>Pour relances et notifications</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <Check className='mt-0.5 h-4 w-4 text-primary' />
                      <span>Compatible avec votre plan principal</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <Check className='mt-0.5 h-4 w-4 text-primary' />
                      <span>Achetez uniquement le volume utile</span>
                    </li>
                  </ul>

                  <div className='mt-6'>
                    <Button variant='outline' className='w-full'>
                      Disponible en option
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className='mx-auto max-w-7xl px-4 pb-12 md:pb-16'>
        <div className='grid gap-4 md:grid-cols-3'>
          <InfoCard
            icon={<BadgeCheck className='h-5 w-5 text-primary' />}
            title='Ce que vous gagnez vraiment'
            desc="Moins d'oublis, plus de visibilité sur les paiements et un stock mieux tenu."
          />
          <InfoCard
            icon={<Sparkles className='h-5 w-5 text-primary' />}
            title='Commencez simple'
            desc='Choisissez le plan logiciel adapté à votre taille actuelle, puis faites évoluer votre usage.'
          />
          <InfoCard
            icon={<MessageSquare className='h-5 w-5 text-primary' />}
            title='Ajoutez WhatsApp selon votre rythme'
            desc='Les relances WhatsApp et les packs viennent compléter votre gestion selon vos besoins réels.'
          />
        </div>
      </section>

      <section className='mx-auto max-w-4xl px-4 pb-12 md:pb-16'>
        <div className='text-center'>
          <Badge variant='secondary'>Questions fréquentes</Badge>
          <h2 className='mt-3 text-3xl font-extrabold tracking-tight'>
            Ce qu'on vous demande souvent
          </h2>
        </div>

        <div className='mt-8 grid gap-4'>
          {PRICING_FAQ.map((faq) => (
            <Card key={faq.q} className='rounded-xl'>
              <CardContent className='p-5'>
                <h3 className='font-semibold'>{faq.q}</h3>
                <p className='mt-2 text-sm text-muted-foreground'>{faq.a}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <FinalCta />
    </div>
  )
}

function PlanCard({ plan }: { plan: Plan }) {
  const { state } = useStore()
  const navigate = useNavigate()
  const requestPlanMut = useRequestPlanMutation()

  const isPending = useMemo(
    () => requestPlanMut.isPending,
    [requestPlanMut.isPending],
  )

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    try {
      await choosePlan({
        plan: plan.backendKey,
        userInfo: state.userInfo,
        requestPlan: (p: PlanKey) => requestPlanMut.mutateAsync(p),
        navigate,
      })
      toast.success(
        'Votre demande a été enregistrée. Suivez les instructions de paiement.',
      )
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Demande impossible')
    }
  }

  return (
    <Card
      className={clsx(
        'rounded-2xl border shadow-sm transition-all',
        plan.featured && 'ring-2 ring-primary/60 shadow-lg',
      )}
    >
      <CardHeader className='relative pb-4'>
        {plan.featured && (
          <div className='absolute -top-7 left-1/2 -translate-x-1/2'>
            <Badge className='rounded-full px-3 py-1'>Le plus équilibré</Badge>
          </div>
        )}

        <div className='text-center'>
          <Badge variant='outline'>{plan.label}</Badge>
          <CardTitle className='mt-3 text-2xl font-extrabold tracking-tight'>
            {plan.name}
          </CardTitle>
          <div className='mt-2 text-3xl font-extrabold'>{plan.priceShort}</div>
          {plan.priceNote ? (
            <p className='mt-1 text-xs text-muted-foreground'>
              {plan.priceNote}
            </p>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className='pb-6'>
        <p className='text-sm text-muted-foreground text-center'>
          {plan.description}
        </p>

        <div className='mt-5 rounded-xl border bg-muted/30 p-4'>
          <div className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
            Idéal pour
          </div>
          <div className='mt-1 text-sm font-medium'>{plan.idealFor}</div>
        </div>

        <ul className='mt-5 space-y-2 text-sm'>
          {plan.highlights.map((feature, index) => (
            <li key={index} className='flex items-start gap-2'>
              <Check className='mt-0.5 h-4 w-4 text-primary' />
              <span>{feature}</span>
            </li>
          ))}
          {(plan.limits ?? []).map((limit, index) => (
            <li
              key={`limit-${index}`}
              className='flex items-start gap-2 text-muted-foreground'
            >
              <Minus className='mt-0.5 h-4 w-4' />
              <span>{limit}</span>
            </li>
          ))}
        </ul>

        <Separator className='my-5' />

        <div className='grid grid-cols-2 gap-3'>
          {plan.specs.map((spec) => (
            <div key={spec.label} className='rounded-xl border p-3'>
              <div className='flex items-center gap-2 text-muted-foreground'>
                {spec.icon}
                <span className='text-xs'>{spec.label}</span>
              </div>
              <div className='mt-2 text-sm font-semibold'>{spec.value}</div>
            </div>
          ))}
        </div>

        <div className='mt-6'>
          <Link to={plan.to} className='block'>
            <Button
              onClick={handleClick}
              disabled={isPending}
              size='lg'
              className='w-full'
              variant={plan.featured ? 'default' : 'outline'}
            >
              {isPending ? 'Traitement...' : plan.cta}
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

function TrustPill({ title, desc }: { title: string; desc: string }) {
  return (
    <div className='rounded-xl border bg-background/80 p-4 text-left shadow-sm'>
      <div className='font-semibold'>{title}</div>
      <div className='mt-1 text-sm text-muted-foreground'>{desc}</div>
    </div>
  )
}

function InfoCard({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode
  title: string
  desc: string
}) {
  return (
    <Card className='rounded-2xl'>
      <CardContent className='p-5'>
        <div className='flex items-center gap-2'>
          {icon}
          <h3 className='font-semibold'>{title}</h3>
        </div>
        <p className='mt-2 text-sm text-muted-foreground'>{desc}</p>
      </CardContent>
    </Card>
  )
}

function PricingRow({
  label,
  monthly,
  yearly,
  lifetime,
}: {
  label: string
  monthly: string
  yearly: string
  lifetime: string
}) {
  return (
    <tr className='border-b last:border-0'>
      <td className='py-3 pr-4 font-medium'>{label}</td>
      <td className='py-3 px-4 text-muted-foreground'>{monthly}</td>
      <td className='py-3 px-4 text-muted-foreground'>{yearly}</td>
      <td className='py-3 pl-4 text-muted-foreground'>{lifetime}</td>
    </tr>
  )
}
